"use client";

import { useEffect, useState } from "react";

/**
 * DevToolsGuard (JSX, desktop + mobile)
 *
 * Deterrent only, not a security boundary.
 * Test with a PRODUCTION build (`next build && next start`), not `next dev`.
 *
 * Props:
 *   enabled          turn the guard on/off (e.g. only in production)
 *   pollInterval     ms between checks (default 1000 desktop, 2000 mobile)
 *   disableSelection block text selection / long-press callout
 *   debug            console.warn which probe fired (for troubleshooting)
 */

const SIZE_THRESHOLD = 160;

function detectMobile() {
  var ua = navigator.userAgent || "";
  var uaMobile =
    /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile|webOS|BlackBerry/i.test(
      ua,
    );
  var ipadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return uaMobile || ipadOS;
}

function isDockedDevToolsOpen() {
  var widthGap = window.outerWidth - window.innerWidth;
  var heightGap = window.outerHeight - window.innerHeight;
  return widthGap > SIZE_THRESHOLD || heightGap > SIZE_THRESHOLD;
}

function isInPageConsolePresent() {
  try {
    if (window.eruda || window.VConsole || window.vConsole) return true;
    if (document.getElementById("eruda")) return true;
    if (document.getElementById("__vconsole")) return true;
    if (document.querySelector(".vc-switch, .eruda-entry-btn")) return true;
  } catch (e) {
    /* ignore */
  }
  return false;
}

/**
 * Runs a `debugger` statement and measures how long it took. It only
 * stalls while devtools is open.
 *
 * IMPORTANT: a literal `debugger;` in source is stripped by Next's
 * production minifier, so we create it at runtime with the Function
 * constructor, which the minifier cannot see.
 */
function debuggerStalls() {
  var start = performance.now();
  try {
    new Function("debugger;")();
  } catch (e) {
    // Strict CSP blocking eval: fall back (may be removed by the minifier).
    // eslint-disable-next-line no-debugger
  }
  return performance.now() - start > 100;
}

export default function DevToolsGuard({
  children,
  enabled = true,
  pollInterval,
  disableSelection = true,
  debug = false,
}) {
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    var mobile = detectMobile();
    var interval = pollInterval || (mobile ? 2000 : 1000);
    // Keep the overlay up briefly after the last detection so it doesn't
    // flicker between probes.
    var holdMs = interval * 3;
    var lastHit = 0;

    function log(reason) {
      if (debug) console.warn("[DevToolsGuard] detected via:", reason);
    }

    // ---------- Right click / long-press ----------
    function onContextMenu(e) {
      e.preventDefault();
    }
    document.addEventListener("contextmenu", onContextMenu);

    // ---------- Keyboard shortcuts ----------
    function onKeyDown(e) {
      var key = (e.key || "").toLowerCase();
      var mod = e.ctrlKey || e.metaKey;
      var block =
        e.key === "F12" ||
        (mod &&
          e.shiftKey &&
          (key === "i" || key === "j" || key === "c" || key === "k")) ||
        (mod && key === "u") ||
        (e.metaKey &&
          e.altKey &&
          (key === "i" || key === "j" || key === "c" || key === "u"));
      if (block) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    window.addEventListener("keydown", onKeyDown, true);

    // ---------- Selection / callout blocking ----------
    var styleEl = null;
    if (disableSelection) {
      styleEl = document.createElement("style");
      styleEl.setAttribute("data-devtools-guard", "");
      styleEl.textContent =
        "html,body{-webkit-touch-callout:none;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;}" +
        "input,textarea,[contenteditable='true']{-webkit-user-select:text;-moz-user-select:text;-ms-user-select:text;user-select:text;}" +
        "img{-webkit-user-drag:none;}";
      document.head.appendChild(styleEl);
    }

    // ---------- Console probe ----------
    // Uses console.log (console.debug is hidden by default in Chrome and
    // never gets evaluated). Only fires when the Console panel/drawer is open.
    var probe = new Image();
    var consoleHit = false;
    try {
      Object.defineProperty(probe, "id", {
        get: function () {
          consoleHit = true;
          return "";
        },
      });
    } catch (e) {
      probe = null;
    }

    function check() {
      var hit = false;

      // 1) Docked devtools via window size (desktop only)
      if (!mobile && isDockedDevToolsOpen()) {
        hit = true;
        log("window size");
      }

      // 2) In-page consoles (Eruda, vConsole)
      if (isInPageConsolePresent()) {
        hit = true;
        log("in-page console");
      }

      // 3) Console probe
      if (probe && typeof console !== "undefined" && console.log) {
        consoleHit = false;
        console.log(probe);
        if (console.clear) console.clear();
        if (consoleHit) {
          hit = true;
          log("console probe");
        }
      }

      // 4) debugger timing probe. Catches devtools on ANY tab, docked or
      // undocked, on desktop and remote-debugged mobile.
      if (debuggerStalls()) {
        hit = true;
        log("debugger timing");
      }

      if (hit) lastHit = Date.now();
      setBlocked(Date.now() - lastHit < holdMs && lastHit !== 0);
    }

    check();
    var timer = window.setInterval(check, interval);
    if (!mobile) window.addEventListener("resize", check);

    return function cleanup() {
      window.clearInterval(timer);
      window.removeEventListener("resize", check);
      document.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown, true);
      if (styleEl && styleEl.parentNode)
        styleEl.parentNode.removeChild(styleEl);
    };
  }, [enabled, pollInterval, disableSelection, debug]);

  if (enabled && blocked) {
    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483647,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
          background: "#14161a",
          color: "#f2f2f2",
          fontFamily: "system-ui, -apple-system, Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: "420px" }}>
          <h1 style={{ fontSize: "22px", margin: "0 0 12px" }}>
            Close developer tools to continue
          </h1>
          <p style={{ lineHeight: 1.6, margin: 0, opacity: 0.8 }}>
            This page is paused while developer tools or a debugging console are
            active. Close them and the page will return on its own.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
